import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Snackbar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Avatar,
  Badge,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  VolumeUp as VolumeIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/UI/PageTitle';

// Типы данных
interface Workstation {
  id: string;
  name: string;
  window_number: number;
  is_active: boolean;
}

interface Queue {
  id: string;
  name: string;
  code: string;
  priority: number;
  waiting_count: number;
  workstation_names: string[];
}

interface Ticket {
  id: string;
  number: string;
  full_number: string;
  queue_name: string;
  queue_code: string;
  status: 'waiting' | 'called' | 'in_service' | 'completed' | 'no_show' | 'cancelled';
  priority: number;
  created_at: string;
  called_at?: string;
  completed_at?: string;
  client_info?: any;
  estimated_wait_time?: number;
}

interface OperatorSession {
  id: string;
  status: 'available' | 'busy' | 'break' | 'offline';
  workstation_id: string;
  workstation_name: string;
  started_at: string;
  total_tickets_served: number;
  queue_ids: string[];
}

export const OperatorPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>('');
  const [sessionStatus, setSessionStatus] = useState<'available' | 'busy' | 'break' | 'offline'>('offline');
  const [workstationDialogOpen, setWorkstationDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [currentTicket, setCurrentTicket] = useState<Ticket | null>(null);

  // Проверяем, нужно ли сменить пароль
  useEffect(() => {
    if (user?.mustChangePassword) {
      setPasswordDialogOpen(true);
    }
  }, [user]);

  // Загрузка рабочих мест
  const { data: workstationsData, isLoading: workstationsLoading } = useQuery(
    ['workstations'],
    async () => {
      const res = await apiClient.get('/api/operators/workstations');
      return res.data;
    }
  );

  // Загрузка очередей
  const { data: queuesData, isLoading: queuesLoading } = useQuery(
    ['queues'],
    async () => {
      const res = await apiClient.get('/api/queues');
      return res.data;
    },
    { refetchInterval: 5000 }
  );

  // Загрузка текущей сессии оператора
  const { data: sessionData, isLoading: sessionLoading } = useQuery(
    ['operator-session'],
    async () => {
      const res = await apiClient.get('/api/operators/session');
      return res.data;
    },
    { refetchInterval: 2000 }
  );

  // Загрузка талонов для текущей очереди
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery(
    ['tickets', selectedWorkstation],
    async () => {
      if (!selectedWorkstation) return { data: { tickets: [] } };
      const res = await apiClient.get(`/api/operators/tickets?workstationId=${selectedWorkstation}`);
      return res.data;
    },
    { 
      enabled: !!selectedWorkstation,
      refetchInterval: 3000 
    }
  );

  // Мутация для смены пароля
  const changePasswordMutation = useMutation(
    (data: { currentPassword: string; newPassword: string }) =>
      apiClient.post('/api/auth/change-password', data),
    {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Пароль успешно изменен', severity: 'success' });
        setPasswordDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка смены пароля', severity: 'error' });
      },
    }
  );

  // Мутация для начала сессии
  const startSessionMutation = useMutation(
    (workstationId: string) =>
      apiClient.post('/api/operators/session/start', { workstationId }),
    {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Сессия начата', severity: 'success' });
        setWorkstationDialogOpen(false);
        queryClient.invalidateQueries(['operator-session']);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка начала сессии', severity: 'error' });
      },
    }
  );

  // Мутация для изменения статуса
  const updateStatusMutation = useMutation(
    (status: string) =>
      apiClient.put('/api/operators/session/status', { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['operator-session']);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка изменения статуса', severity: 'error' });
      },
    }
  );

  // Мутация для вызова талона
  const callTicketMutation = useMutation(
    (ticketId: string) =>
      apiClient.post(`/api/operators/tickets/${ticketId}/call`),
    {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Талон вызван', severity: 'success' });
        queryClient.invalidateQueries(['tickets']);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка вызова талона', severity: 'error' });
      },
    }
  );

  // Мутация для завершения талона
  const completeTicketMutation = useMutation(
    (ticketId: string) =>
      apiClient.post(`/api/operators/tickets/${ticketId}/complete`),
    {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Талон завершен', severity: 'success' });
        setCurrentTicket(null);
        queryClient.invalidateQueries(['tickets']);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка завершения талона', severity: 'error' });
      },
    }
  );

  // Обработчики
  const handleStartSession = () => {
    if (selectedWorkstation) {
      startSessionMutation.mutate(selectedWorkstation);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    updateStatusMutation.mutate(newStatus);
  };

  const handleCallTicket = (ticket: Ticket) => {
    callTicketMutation.mutate(ticket.id);
    setCurrentTicket(ticket);
  };

  const handleCompleteTicket = () => {
    if (currentTicket) {
      completeTicketMutation.mutate(currentTicket.id);
    }
  };

  const handleChangePassword = () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'Пароли не совпадают', severity: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      setSnackbar({ open: true, message: 'Пароль должен содержать минимум 8 символов', severity: 'error' });
      return;
    }
    changePasswordMutation.mutate({ currentPassword: '', newPassword });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Если нужно сменить пароль
  if (passwordDialogOpen) {
    return (
      <>
        <PageTitle title="Смена пароля - ELEKTROM" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Card sx={{ maxWidth: 400, width: '100%', m: 2 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom align="center">
                Смена пароля
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Для продолжения работы необходимо сменить пароль
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Новый пароль"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                type="password"
                label="Подтвердите пароль"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleChangePassword}
                disabled={changePasswordMutation.isLoading}
              >
                {changePasswordMutation.isLoading ? 'Смена...' : 'Сменить пароль'}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  // Если нет активной сессии
  if (!sessionData?.data?.session && !workstationDialogOpen) {
    return (
      <>
        <PageTitle title="Выбор рабочего места - ELEKTROM" />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Card sx={{ maxWidth: 500, width: '100%', m: 2 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom align="center">
                Выберите рабочее место
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Для начала работы выберите кассу/окно
              </Typography>
              
              {workstationsLoading ? (
                <LinearProgress />
              ) : (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Рабочее место</InputLabel>
                  <Select
                    value={selectedWorkstation}
                    onChange={(e) => setSelectedWorkstation(e.target.value)}
                    label="Рабочее место"
                  >
                    {workstationsData?.data?.workstations?.map((workstation: Workstation) => (
                      <MenuItem key={workstation.id} value={workstation.id}>
                        {workstation.name} (Окно {workstation.window_number})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <Button
                fullWidth
                variant="contained"
                onClick={() => setWorkstationDialogOpen(true)}
                disabled={!selectedWorkstation || workstationsLoading}
                startIcon={<ComputerIcon />}
              >
                Начать работу
              </Button>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  const session = sessionData?.data?.session;
  const queues = queuesData?.data?.queues || [];
  const tickets = ticketsData?.data?.tickets || [];

  return (
    <>
      <PageTitle title="Рабочее место оператора - ELEKTROM" />
      
      <Box sx={{ p: 3 }}>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Рабочее место оператора
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {user?.firstName} {user?.lastName} • {session?.workstation_name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить">
              <IconButton onClick={() => queryClient.invalidateQueries()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Настройки">
              <IconButton>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Выход">
              <IconButton onClick={handleLogout} color="error">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Статус оператора */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Статус оператора
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: session?.status === 'available' ? 'success.main' : 'warning.main', mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1">
                      {session?.status === 'available' ? 'Доступен' : 
                       session?.status === 'busy' ? 'Занят' : 
                       session?.status === 'break' ? 'Перерыв' : 'Офлайн'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Обслужено: {session?.total_tickets_served || 0} талонов
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={session?.status === 'available' ? 'contained' : 'outlined'}
                    onClick={() => handleStatusChange('available')}
                    startIcon={<PlayIcon />}
                  >
                    Доступен
                  </Button>
                  <Button
                    size="small"
                    variant={session?.status === 'busy' ? 'contained' : 'outlined'}
                    onClick={() => handleStatusChange('busy')}
                    startIcon={<PauseIcon />}
                  >
                    Занят
                  </Button>
                  <Button
                    size="small"
                    variant={session?.status === 'break' ? 'contained' : 'outlined'}
                    onClick={() => handleStatusChange('break')}
                    startIcon={<StopIcon />}
                  >
                    Перерыв
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Текущий талон */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Текущий талон
                </Typography>
                {currentTicket ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" color="primary" gutterBottom>
                      {currentTicket.full_number}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {currentTicket.queue_name}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={handleCompleteTicket}
                      disabled={completeTicketMutation.isLoading}
                      startIcon={<CheckIcon />}
                    >
                      Завершить
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Нет активного талона
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Статистика */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Статистика
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">В очереди:</Typography>
                    <Typography variant="h6" color="warning.main">
                      {tickets.filter(t => t.status === 'waiting').length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Вызвано:</Typography>
                    <Typography variant="h6" color="info.main">
                      {tickets.filter(t => t.status === 'called').length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Обслужено:</Typography>
                    <Typography variant="h6" color="success.main">
                      {session?.total_tickets_served || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Очереди */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Доступные очереди
                </Typography>
                <List>
                  {queues.map((queue: Queue) => (
                    <ListItem key={queue.id} divider>
                      <ListItemText
                        primary={queue.name}
                        secondary={`${queue.code} • Приоритет: ${queue.priority}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={queue.waiting_count}
                          color="warning"
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Талоньы в очереди */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Талоньы в очереди
                </Typography>
                {ticketsLoading ? (
                  <LinearProgress />
                ) : (
                  <List>
                    {tickets.slice(0, 10).map((ticket: Ticket) => (
                      <ListItem key={ticket.id} divider>
                        <ListItemText
                          primary={`${ticket.full_number} - ${ticket.queue_name}`}
                          secondary={`Создан: ${new Date(ticket.created_at).toLocaleTimeString()}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleCallTicket(ticket)}
                            disabled={callTicketMutation.isLoading || ticket.status !== 'waiting'}
                            startIcon={<VolumeIcon />}
                          >
                            Вызвать
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                    {tickets.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Нет талонов в очереди"
                          secondary="Все талоны обработаны"
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Диалог выбора рабочего места */}
        <Dialog open={workstationDialogOpen} onClose={() => setWorkstationDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Выберите рабочее место</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Рабочее место</InputLabel>
              <Select
                value={selectedWorkstation}
                onChange={(e) => setSelectedWorkstation(e.target.value)}
                label="Рабочее место"
              >
                {workstationsData?.data?.workstations?.map((workstation: Workstation) => (
                  <MenuItem key={workstation.id} value={workstation.id}>
                    {workstation.name} (Окно {workstation.window_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWorkstationDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleStartSession}
              disabled={!selectedWorkstation || startSessionMutation.isLoading}
            >
              {startSessionMutation.isLoading ? 'Начало...' : 'Начать работу'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Уведомления */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
